import React, { FC, IframeHTMLAttributes, useEffect, useState } from "react";
import { handleGetSignatureOnRamper } from "../../lib/getSignature";

const ONRAMPER_URL = "https://buy.onramper.com/";

interface AddressMap {
  BTC: string;
  BCH: string;
  ETH: string;
  SOL: string;
  POL: string;
  BSC: string;
  DOGE: string;
  XRP: string;
  AVAX: string;
  [key: string]: string;
}

interface StytchUser {
  user_id: string;
  emails: Array<{ email: string; email_id: string; verified: boolean }>;
  phone_numbers: Array<{ phone_number: string }>;
  name?: {
    first_name?: string;
    last_name?: string;
  };
}

interface Props {
  crypto?: string;
  amount?: string;
  fiat?: string;
  isSell?: boolean;
  addresses: AddressMap | null;
  user: StytchUser | null;
}

const OnRamperWidget: FC<Props & IframeHTMLAttributes<HTMLIFrameElement>> = ({
  crypto,
  amount,
  fiat,
  isSell,
  addresses,
  user,
  ...rest
}) => {
  const [frameSrc, setFrameSrc] = useState("");
  type CryptoItem = Record<string, string>;

  useEffect(() => {
    const createIFrameSrc = async () => {
      if (!addresses) return;

      const cryptolist: CryptoItem[] = [
        {"BTC": "btc"},
        {"ETH": "eth"},
        {"BCH": "bch_bitcoincash"},
        {"AVAX": "avax_avaxx"},
        {"BAT": "bat_ethereum"},
        {"BNB": "bnb_ethereum"},
        {"COMP": "comp_ethereum"},
        {"DASH": "dash_dash"},
        {"DOGE": "doge_dogecoin"},
        {"DOT": "dot_ethereum"},
        {"GUSD": "gusd_ethereum"},
        {"LTC": "ltc_bbc"},
        {"POL": "pol_polygon"},
        {"SHIB": "shib_ethereum"},
        {"SUSHI": "sushi_ethereum"},
        {"UNI": "uni_ethereum"},
        {"USDC": "usdc_ethereum"},
        {"USDT": "usdt_ethereum"},
        {"WBTC": "wbtc_ethereum"},
        {"XLM": "xlm_bsc"},
        {"XRP": "xrp_ethereum"},
      ];

      const query = new URLSearchParams();

      const txnType = isSell ? "sell" : "buy";

      query.append("apiKey", process.env.ONRAMPER_API_KEY ?? '');

      const walletAddress = [
        `bitcoin:${addresses.BTC}`,
        `bitcoincash:${addresses.BCH}`,
        `ethereum:${addresses.ETH}`,
        `solana:${addresses.SOL}`,
        `polygon:${addresses.POL}`,
        `bsc:${addresses.BSC}`,
        `dogecoin:${addresses.DOGE}`,
        `ripple:${addresses.XRP}`,
        `avaxc:${addresses.AVAX}`,
      ].join(',');

      query.append("networkWallets", walletAddress);

      const signature = await handleGetSignatureOnRamper(
        `networkWallets=${walletAddress}`,
      )
      query.append("signature", signature);

      query.append("uid", user?.user_id || '');

      const getCryptoValue = (cryptoKey: string): string | null => {
        const cryptoObject = cryptolist.find(item => item[cryptoKey]);
        return cryptoObject ? cryptoObject[cryptoKey] : null;
      };

      if (amount) query.append(txnType === "sell" ? "sell_defaultAmount" : "defaultAmount", amount);
      if (fiat) query.append(txnType === "sell" ? "sell_defaultFiat" : "defaultFiat", fiat);
      if (crypto) query.append(txnType === "sell" ? "sell_defaultCrypto" : "defaultCrypto", getCryptoValue(crypto) ?? '');

      const url = `${ONRAMPER_URL}?${query.toString()}&themeName=bluey&mode=${txnType === "sell" ? "sell,buy" : "buy,sell"}&primaryColor=2793ff&borderRadius=1&wgBorderRadius=1`;

      setFrameSrc(url);
    };

    createIFrameSrc();
  }, [crypto, amount, fiat, isSell, addresses, user?.user_id]);

  return (
    <iframe
      {...rest}
      title="OnRamper Widget"
      allow="accelerometer; autoplay; camera; gyroscope; payment; microphone; geolocation"
      height="100%"
      width="100%"
      style={{ border: 0, ...rest.style }}
      src={frameSrc}
    />
  );
};
export default OnRamperWidget;
